import { CdkListbox } from '@angular/cdk/listbox';
import { CdkConnectedOverlay, CdkOverlayOrigin, Overlay, ScrollStrategy } from '@angular/cdk/overlay';
import { NgClass, NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  linkedSignal,
  OnInit,
  Output,
  TemplateRef,
  ViewChild,
  WritableSignal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ControlValueAccessor, FormsModule, NgControl } from '@angular/forms';
import { CslCdkListboxDirective } from '../../directives/csl-cdk-listbox/csl-cdk-listbox.directive';
import { InputDirective } from '../../directives/input/input.directive';

import { LiveAnnouncer } from '@angular/cdk/a11y';
import { DOWN_ARROW, ENTER, hasModifierKey, LEFT_ARROW, RIGHT_ARROW, SPACE, UP_ARROW } from '@angular/cdk/keycodes';
import { IconComponent } from '@shared/ui-shared';
import { IconsPathProvider } from '../../providers/icons-path.provider';
import { transformPanel } from './select-animations';

const CDK_IMPORTS = [CdkConnectedOverlay, CdkOverlayOrigin];
const CSL_IMPORTS = [InputDirective, IconComponent];

@Component({
  selector: 'csl-select',
  standalone: true,
  imports: [FormsModule, NgClass, ...CDK_IMPORTS, ...CSL_IMPORTS, NgTemplateOutlet],
  templateUrl: './select.component.html',
  styleUrls: ['./select.component.css', '../../../styles/dropdown.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  hostDirectives: [{ directive: CslCdkListboxDirective, inputs: ['cdkListboxMultiple: multiple'] }],
  animations: [transformPanel],
  providers: [{ provide: CdkListbox, useExisting: CslCdkListboxDirective }, IconsPathProvider],
  host: {
    class: 'block focus:outline-none',
    role: 'combobox',
    'aria-haspopup': 'listbox',
    '[attr.aria-controls]': 'isOpen ? id + "-panel" : null',
    '[attr.aria-owns]': 'isOpen ? id + "-panel" : null',
    '[attr.aria-expanded]': 'isOpen',
    '[attr.tabindex]': 'isDisabled ? -1 : 0',
    '(focusin)': 'focus();',
    '(focusout)': 'blur();',
    '(mousedown)': 'isDisabled ? null : (isOpen ? close() : open())',
    '(keydown)': '_handleKeydown($event)',
  },
})
export class SelectComponent<T> implements ControlValueAccessor, OnInit {
  BATCH_LOW_THRESHOLD = 100; // if the number of options is greater than this threshold, the component will batch value changes to improve performance
  private cdr = inject(ChangeDetectorRef);
  private listbox = inject(CslCdkListboxDirective);
  private ngControl = inject(NgControl, { optional: true, self: true });
  private _liveAnnouncer = inject(LiveAnnouncer);
  private elementRef = inject(ElementRef);

  private overlay = inject(Overlay);

  protected closeScrollStrategy: ScrollStrategy = this.overlay.scrollStrategies.close();

  isOpen = false;
  isFocused = false;
  isDisabled = false;

  searchInput?: HTMLInputElement;

  @ViewChild('dropdownPanel') dropdownPanel!: ElementRef<HTMLElement>;

  @Input() id: string | undefined;
  @Input() placeholder: string | undefined;
  @Input() searchPlaceholder = 'Search items';
  @Input() isLoading = false;
  @Input() withSearch = false;
  @Input() multiple = false;
  @Input({ transform: (v: string | number) => +v }) density: 1 | 0 = 1;
  @Input() displayValue?: ((value: readonly T[]) => string) | 'option';
  @Input() displayTemplate?: TemplateRef<any>;
  @Input() customCdkClass = '';
  @Input() customDropdownClass = '';
  @Input() compareWith: ((o1: any, o2: any) => boolean) | undefined = undefined;
  @Input() disableSelectOnSpace = false; // if true, space key will not select the option when the dropdown is open

  @Input() batching = false; // if true, the component will batch multiple value changes within the same tick into a single change detection cycle. This can improve performance when making multiple updates to the select value in a short period of time.
  @Input() inverted = false;

  @Output() focusChange = new EventEmitter<boolean>();
  @Output() searchText = new EventEmitter<string>();
  @Output() dropdownClosed = new EventEmitter<void>();

  private isWritingValue = false;
  private restoreFocus = true;

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private _onChange: (value: T | readonly T[] | null) => void = () => {};
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private _onTouched: () => void = () => {};

  constructor() {
    if (this.ngControl) {
      // Note: we provide the value accessor through here, instead of
      // the `providers` to avoid running into a circular import.
      this.ngControl.valueAccessor = this;
    }
    this.listbox.mode = 'select';
    this.listbox.valueChange.pipe(takeUntilDestroyed()).subscribe((v) => {
      if (this.multiple) {
        const emittedValues = v.value.filter(Boolean) as T[];
        const currentControlValue = this.ngControl?.control?.value;
        const currentValues = (
          Array.isArray(currentControlValue) ? currentControlValue : (this.selected() as readonly T[] | undefined) || []
        ) as readonly T[];
        const compareWith = this.compareWith;
        const isSame = (a: T, b: T) => (compareWith ? compareWith(a, b) : a === b);

        if (this.batching && this.isWritingValue && !v.option) {
          this.selected.set(currentValues);
          return;
        }

        // In batching mode, some emissions have no explicit toggled option.
        // Only ignore them when they don't imply a real change for currently rendered options.
        if (this.batching && !v.option) {
          const renderedValues =
            (this.listbox.items
              ?.map((opt) => opt.value)
              .filter((value): value is T => value !== null && value !== undefined) as T[] | undefined) || [];

          const isRenderedValue = (value: T) => renderedValues.some((rendered) => isSame(rendered, value));

          const removed = currentValues.filter(
            (current) => !emittedValues.some((emitted) => isSame(emitted, current as T)),
          );
          const added = emittedValues.filter(
            (emitted) => !currentValues.some((current) => isSame(current as T, emitted)),
          );

          const renderedRemoved = removed.filter((value) => isRenderedValue(value as T)) as T[];
          const renderedAdded = added.filter((value) => isRenderedValue(value as T)) as T[];

          if (!renderedRemoved.length && !renderedAdded.length) {
            this.selected.set(currentValues);
            return;
          }

          const nextValues = currentValues.filter(
            (value) => !renderedRemoved.some((removedValue) => isSame(removedValue, value as T)),
          ) as T[];

          renderedAdded.forEach((addedValue) => {
            if (!nextValues.some((value) => isSame(value as T, addedValue))) {
              nextValues.push(addedValue);
            }
          });

          this._onChange(nextValues.length ? nextValues : null);
          this.selected.set(nextValues);
          return;
        }

        let nextValues = emittedValues;

        // In virtualized/infinite lists the emitted value can be only the currently rendered subset.
        // For user toggles, reconcile from current form value + toggled option to preserve off-screen selections.
        if (v.option && v.option.value !== null && v.option.value !== undefined) {
          const optionValue = v.option.value as T;
          const exists = currentValues.some((value) => isSame(value as T, optionValue));

          if (v.option.isSelected()) {
            nextValues = exists ? [...currentValues] : [...currentValues, optionValue];
          } else {
            // In virtualized lists, stale internal state can emit a deselect
            // for an option that is not present in the current form value.
            // Treat that contradictory emission as a select intent.
            nextValues = exists
              ? (currentValues.filter((value) => !isSame(value as T, optionValue)) as T[])
              : [...currentValues, optionValue];
          }
        }

        this._onChange(nextValues.length ? nextValues : null);
        this.selected.set(nextValues);
      } else {
        this._onChange(v.value[0]); // emit single value instead of provided array
        this.selected.set(v.value);
        // Announce explicitly instead of relying on aria-activedescendant, which can go stale once the popup closes.
        this._liveAnnouncer.announce(this.getDisplayValue(), 10000);
        this.close();
      }
    });
  }

  ngOnInit(): void {
    this.listbox.compareWith = this.compareWith;
  }

  selected: WritableSignal<readonly T[] | undefined> = linkedSignal<readonly T[] | undefined>(() =>
    this.getSelectedValues(),
  );

  getSelectedValues() {
    return this.listbox.value.filter(Boolean);
  }

  getDisplayValue() {
    const value = this.listbox.value;
    if (this.displayValue instanceof Function) {
      return this.displayValue(value);
    }
    const selectedOptions = this.listbox.items?.filter((opt) => opt.isSelected()) || [];
    if (this.displayValue === 'option') {
      if (this.multiple) {
        return selectedOptions
          .filter((opt) => opt.value)
          .map((opt) => opt.element.innerText)
          .join(', ');
      } else {
        return selectedOptions[0]?.element?.innerText;
      }
    }
    return this.multiple ? value.filter(Boolean).join(', ') : value.toString();
  }

  open() {
    if (this.isDisabled) return;
    this.isOpen = true;
  }

  close(restoreFocus = true) {
    this.isOpen = false;
    this.restoreFocus = restoreFocus;
    if (restoreFocus) {
      this.focusHost();
    }
  }

  focus() {
    if (this.isDisabled) return;
    this.isFocused = true;
    this.focusChange.emit(this.isFocused);
  }

  blur() {
    this.isFocused = false;
    this.focusChange.emit(this.isFocused);
    if (!this.isOpen) {
      if (!this.batching) {
        this.clearSearch();
      }
      this._onTouched();
    }
  }

  onDropdownClose() {
    this.dropdownClosed.emit();
    if (!this.batching) {
      this.clearSearch();
    }
    if (!this.isFocused) {
      this._onTouched();
    }

    this.close(this.restoreFocus);
  }

  private focusHost(): void {
    this.elementRef.nativeElement.focus();
  }

  clearSearch() {
    this.searchInput && (this.searchInput.value = '');
    this.searchText.next('');
  }

  deselectValue(value: T[]): void {
    if (!this.multiple) {
      console.error('deselectValue is only available in multiple selection mode');
      return;
    }

    const compareWith = this.compareWith;
    const options = this.listbox.items?.filter((opt) =>
      compareWith ? value.find((v) => compareWith(opt.value, v)) : value.includes(opt.value),
    );

    options?.forEach((opt) => this.listbox.deselect(opt));
  }

  writeValue(value: T | readonly T[] | null): void {
    if (this.batching) {
      this.isWritingValue = true;
    }
    try {
      if (this.multiple) {
        if (Array.isArray(value)) {
          if (!this.listbox.items?.length) {
            const initialValues = (value as readonly T[]).filter((v) => v !== null && v !== undefined);
            if (initialValues.length) {
              this.listbox.selectValue(...initialValues);
            }

            this.selected.set(this.listbox.value);

            return;
          }

          const compareWith = this.compareWith;
          if (this.batching) {
            const nextValues = (value as readonly T[]).filter((v) => v !== null && v !== undefined);

            if (!compareWith) {
              const nextSet = new Set(nextValues);

              this.listbox.items?.forEach((opt) => {
                const currentlySelected = opt.isSelected();
                const shouldBeSelected = nextSet.has(opt.value);

                if (currentlySelected && !shouldBeSelected) {
                  this.listbox.deselect(opt);
                } else if (!currentlySelected && shouldBeSelected) {
                  opt.select();
                }
              });
            } else {
              // Fast path for object values identified by `id` (towns and similar lists).
              const hasId = (v: unknown): v is { id: unknown } => {
                return v !== null && typeof v === 'object' && Object.prototype.hasOwnProperty.call(v, 'id');
              };

              const canUseIdFastPath = nextValues.every((v) => hasId(v));

              if (canUseIdFastPath) {
                const nextIds = new Set(nextValues.filter(hasId).map((v) => v.id));

                this.listbox.items?.forEach((opt) => {
                  const optValue = opt.value as unknown;
                  const optId = hasId(optValue) ? optValue.id : undefined;

                  const currentlySelected = opt.isSelected();
                  const shouldBeSelected = optId !== undefined && nextIds.has(optId);

                  if (currentlySelected && !shouldBeSelected) {
                    this.listbox.deselect(opt);
                  } else if (!currentlySelected && shouldBeSelected) {
                    opt.select();
                  }
                });
              } else {
                // Generic fallback for custom comparators.
                this.listbox.items?.forEach((opt) => {
                  const currentlySelected = opt.isSelected();
                  const shouldBeSelected = nextValues.some((v) => compareWith(v, opt.value));

                  if (currentlySelected && !shouldBeSelected) {
                    this.listbox.deselect(opt);
                  } else if (!currentlySelected && shouldBeSelected) {
                    opt.select();
                  }
                });
              }
            }

            this.selected.set(compareWith ? nextValues : this.listbox.value?.filter(Boolean));
            this.cdr.markForCheck();
          } else {
            if (compareWith) {
              const newValues = (value as readonly T[]).filter(Boolean);

              this.listbox.items?.forEach((opt) => {
                const currentlySelected = opt.isSelected();
                const shouldBeSelected = newValues.some((v) => compareWith(v, opt.value));

                if (currentlySelected && !shouldBeSelected) {
                  this.listbox.deselect(opt);
                } else if (!currentlySelected && shouldBeSelected) {
                  opt.select();
                }
              });
              this.selected.set(newValues);
            } else {
              this.listbox.items?.forEach((opt) => {
                this.listbox.deselect(opt);
                if (value.includes(opt.value)) {
                  opt.select();
                }
              });
              this.selected.set(this.listbox.value?.filter(Boolean));
            }
          }
        } else if (value === null) {
          this.listbox.setAllSelected(false);
          this.selected.set([]);
        } else {
          console.error(`Multiple selection mode requires an array`);
        }
      } else {
        this.listbox.selectValue(value);
        this.selected.set(this.getSelectedValues());
      }
    } finally {
      if (this.batching) {
        this.isWritingValue = false;
      }
    }
  }

  registerOnChange(fn: (value: T | readonly T[] | null) => void): void {
    this._onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this._onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
    this.listbox.disabled = isDisabled; // syncs aria-disabled/tabindex handled by CdkListbox host bindings
    this.cdr.detectChanges();
  }

  _handleKeydown(event: KeyboardEvent): void {
    if (!this.isDisabled) {
      this.isOpen ? this._handleOpenKeydown(event) : this._handleClosedKeydown(event);
    }
  }

  /** Handles keyboard events while the select is closed. */
  private _handleClosedKeydown(event: KeyboardEvent): void {
    const keyCode = event.keyCode;
    const isArrowKey =
      keyCode === DOWN_ARROW || keyCode === UP_ARROW || keyCode === LEFT_ARROW || keyCode === RIGHT_ARROW;
    const isOpenKey = keyCode === ENTER || (!this.disableSelectOnSpace && keyCode === SPACE);
    const manager = this.listbox.keyManager;

    // Open the select on ALT + arrow key to match the native <select>
    if ((!manager.isTyping() && isOpenKey && !hasModifierKey(event)) || (event.altKey && isArrowKey)) {
      event.preventDefault(); // prevents the page from scrolling down when pressing space
      this.open();
    } else {
      const previouslySelectedOption = manager.activeItem;
      manager.onKeydown(event);
      const selectedOption = manager.activeItem;

      if (selectedOption && previouslySelectedOption !== selectedOption) {
        manager.activeItem?.select();
        this.listbox.selectActiveOptionValue(); // triggers valueChange, which announces the new value
      }
    }
  }

  private _handleOpenKeydown(event: KeyboardEvent): void {
    const manager = this.listbox.keyManager;
    const keyCode = event.keyCode;
    const isArrowKey = keyCode === DOWN_ARROW || keyCode === UP_ARROW;
    const isTyping = manager.isTyping();

    if (isArrowKey && event.altKey) {
      // Close the select on ALT + arrow key to match the native <select>
      event.preventDefault();
      this.close();
      // Don't do anything in this case if the user is typing,
      // because the typing sequence can include the space key.
    } else if (
      !isTyping &&
      (keyCode === ENTER || (!this.disableSelectOnSpace && keyCode === SPACE)) &&
      manager.activeItem &&
      !hasModifierKey(event)
    ) {
      event.preventDefault();
      if (this.multiple) {
        manager.activeItem.toggle();
      } else {
        manager.activeItem.select();
      }
      this.listbox.selectActiveOptionValue();
    } else {
      const previouslyActiveOption = manager.activeItem;
      manager.onKeydown(event);

      // No aria-activedescendant is rendered (see CslCdkListboxDirective), so announce navigation explicitly.
      if (manager.activeItem && manager.activeItem !== previouslyActiveOption) {
        this._liveAnnouncer.announce(manager.activeItem.getLabel(), 10000);
      }
    }
  }
}
